import { useNav } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Pill, SectionLabel } from "../components/ui";
import { Ic } from "../components/icons";
import { PublicHeader } from "../components/PublicHeader";
import { openCookieSettings } from "../lib/consent";

export default function Brand() {
  const { go } = useNav();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-canvas">
      <PublicHeader active="brand" />

      {/* Hero — étymologie & promesse */}
      <section className="max-w-[1040px] mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-12 sm:pb-16">
        <Pill tone="lime" icon={<Ic.Bolt size={12} />} className="mb-6">
          Notre histoire · 10 siècles d'arpentage
        </Pill>
        <h1 className="text-[34px] sm:text-[52px] lg:text-[64px] leading-[1.05] font-semibold tracking-[-0.02em] text-ink-900">
          L'arpenteur{" "}
          <span className="font-serif italic font-normal">augmenté</span>.
          <br />
          Le cadastre qui parle.
        </h1>
        <p className="mt-6 text-[18px] text-ink-700 max-w-[680px] leading-relaxed">
          Depuis le XI<sup>e</sup> siècle, l'arpenteur fait le même métier&nbsp;:
          mesurer la terre, lire ses contraintes, révéler son potentiel.
          <strong className="text-ink-900"> Arpentia reprend ce flambeau</strong>{" "}
          avec une IA qui lit le PLU, croise le cadastre et analyse les DVF — en
          30 secondes, depuis votre smartphone.
        </p>

        {/* Décomposition du nom */}
        <Card className="mt-10 p-6 sm:p-8 bg-paper">
          <SectionLabel>Anatomie du nom</SectionLabel>
          <div className="mt-5 flex flex-wrap items-baseline gap-x-2">
            <span className="text-[44px] sm:text-[56px] font-semibold tracking-tight text-ink-900">
              Arpent
            </span>
            <span className="text-[44px] sm:text-[56px] font-serif italic text-lime-700">
              ia
            </span>
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-4 sm:gap-8">
            <div>
              <div className="text-[12px] uppercase tracking-[0.14em] text-ink-500 font-medium">
                Arpent
              </div>
              <p className="mt-2 text-[14px] text-ink-700 leading-relaxed">
                L'unité ancestrale française de mesure foncière. La rigueur du
                géomètre, le métier d'expert qui sait lire la terre.
              </p>
            </div>
            <div>
              <div className="text-[12px] uppercase tracking-[0.14em] text-ink-500 font-medium">
                IA
              </div>
              <p className="mt-2 text-[14px] text-ink-700 leading-relaxed">
                L'Intelligence Artificielle qui automatise le travail
                d'analyse — PLU, cadastre, DVF, risques, servitudes.
              </p>
            </div>
          </div>
        </Card>
      </section>

      {/* Lignée du mot — frise */}
      <section className="bg-paper/60 border-y border-ink-200/60">
        <div className="max-w-[1040px] mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <SectionLabel>Lignée du mot</SectionLabel>
          <h2 className="mt-3 text-[24px] sm:text-[34px] leading-tight tracking-tight font-semibold max-w-[720px]">
            De la perche gauloise à la{" "}
            <span className="font-serif italic font-normal">requête IA</span>.
          </h2>

          <ol className="mt-10 space-y-6">
            <TimelineItem
              year="≈ ‑200"
              era="Gaulois"
              title="are-penno-"
              body="« fin, extrémité d'un champ ». La racine désigne déjà la limite, ce qui sépare une parcelle d'une autre."
            />
            <TimelineItem
              year="Ier–Ve s."
              era="Latin tardif"
              title="arepennis"
              body="Un demi-jugère, ancienne mesure romaine. La terre devient comptable, mesurable, taxable."
            />
            <TimelineItem
              year="1130"
              era="Vieux français"
              title="arpent"
              body="Première attestation écrite. Le mot s'installe dans les actes notariés et le droit foncier."
            />
            <TimelineItem
              year="1554"
              era="France royale"
              title="Arpenteurs royaux"
              body="Henri II nomme des arpenteurs en Bretagne pour contrôler le cadastre local et vérifier l'impôt foncier. C'est exactement ce qu'Arpentia automatise 500 ans plus tard."
              highlight
            />
            <TimelineItem
              year="XX–XXIe"
              era="Droit moderne"
              title="Document d'arpentage (DMPC)"
              body="Acte juridique officiel rédigé par un géomètre-expert pour acter toute modification de parcelle. Arpentia parle la langue native de ses clients."
            />
            <TimelineItem
              year="2026"
              era="Arpentia"
              title="L'IA qui arpente"
              body="30 secondes, une adresse, une fiche complète. Le métier reste le même — l'outil change."
              highlight
              last
            />
          </ol>
        </div>
      </section>

      {/* Diffusion internationale */}
      <section className="max-w-[1040px] mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
          <div>
            <SectionLabel>Une racine francophone</SectionLabel>
            <h2 className="mt-3 text-[24px] sm:text-[34px] leading-tight tracking-tight font-semibold">
              L'arpent a traversé les{" "}
              <span className="font-serif italic font-normal">océans</span>.
            </h2>
            <p className="mt-5 text-[15px] text-ink-700 leading-relaxed">
              Là où la francophonie s'est implantée, l'arpent reste une unité
              de mesure légale. Arpentia peut s'exporter sans trahir son ADN
              français.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <RegionCard place="Québec" note="Unité légale, encore utilisée" />
            <RegionCard place="Louisiane" note="Présent dans les titres de propriété" />
            <RegionCard place="Île Maurice" note="Unité légale agricole" />
            <RegionCard place="Seychelles" note="Unité légale agricole" />
          </div>
        </div>
      </section>

      {/* Pont vers la value prop */}
      <section className="bg-ink-900 text-white">
        <div className="max-w-[1040px] mx-auto px-4 sm:px-6 py-14 sm:py-24">
          <SectionLabel className="!text-lime-300">
            Ce que ça change pour vous
          </SectionLabel>
          <h2 className="mt-3 text-[26px] sm:text-[40px] leading-tight tracking-tight font-semibold max-w-[760px]">
            Vous ne vendez plus des biens. Vous vendez des{" "}
            <span className="font-serif italic font-normal">potentiels</span>.
          </h2>
          <p className="mt-5 text-[16px] text-ink-300 leading-relaxed max-w-[640px]">
            En 1554, l'arpenteur royal débarquait avec sa perche, sa corde et
            son autorité. En 2026, vous arrivez en RDV mandat avec une fiche
            Arpentia&nbsp;: chiffre clé d'extension, justification réglementaire,
            comparables DVF. La même fonction. Le même effet sur le client.
          </p>

          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            <DarkStat
              kpi="10 siècles"
              label="d'arpentage français à votre service"
            />
            <DarkStat
              kpi="30 secondes"
              label="pour générer une fiche complète"
            />
            <DarkStat
              kpi="34 000+"
              label="communes couvertes en France"
            />
          </div>

          <div className="mt-10 flex items-center gap-3 flex-wrap">
            <Button
              variant="primary"
              size="lg"
              iconRight={<Ic.Arrow size={14} />}
              onClick={() => go(user ? "nouvelle" : "landing")}
            >
              Essayer Arpentia
            </Button>
            <Button
              variant="ghost"
              size="lg"
              className="!text-white hover:!bg-ink-800"
              onClick={() => go("pricing")}
            >
              Voir les tarifs
            </Button>
          </div>
        </div>
      </section>

      {/* Baseline + signature */}
      <section className="max-w-[1040px] mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
        <div className="text-[12px] uppercase tracking-[0.14em] text-ink-500 font-medium">
          Notre promesse
        </div>
        <p className="mt-6 text-[28px] sm:text-[42px] font-serif italic leading-tight text-ink-900 max-w-[820px] mx-auto">
          « Révélez le potentiel caché de chaque bien. »
        </p>
        <div className="mt-6 text-[13px] text-ink-500">
          Arpentia · l'arpenteur du XXI<sup>e</sup> siècle
        </div>
      </section>

      <footer className="border-t border-ink-200/60">
        <div className="max-w-[1240px] mx-auto px-4 sm:px-6 py-8 flex items-center justify-between flex-wrap gap-4 text-[12px] text-ink-500">
          <span>© 2026 Arpentia SAS — 8 rue d'Uzès, 75002 Paris.</span>
          <div className="flex items-center gap-4 sm:gap-5 flex-wrap">
            <button
              onClick={() => {
                window.location.hash = "#mentions-legales";
                go("legal");
              }}
              className="hover:text-ink-900 transition"
            >
              Mentions légales
            </button>
            <button
              onClick={() => {
                window.location.hash = "#cgu";
                go("legal");
              }}
              className="hover:text-ink-900 transition"
            >
              CGU
            </button>
            <button
              onClick={() => {
                window.location.hash = "#confidentialite";
                go("legal");
              }}
              className="hover:text-ink-900 transition"
            >
              Confidentialité
            </button>
            <button
              onClick={openCookieSettings}
              className="hover:text-ink-900 transition"
            >
              Gérer mes cookies
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ---------------- Helpers ---------------- */

function TimelineItem({
  year,
  era,
  title,
  body,
  highlight,
  last,
}: {
  year: string;
  era: string;
  title: string;
  body: string;
  highlight?: boolean;
  last?: boolean;
}) {
  return (
    <li className="grid grid-cols-[120px_1fr] gap-4 sm:gap-8 relative">
      <div>
        <div className="font-mono text-[12px] text-ink-500">{year}</div>
        <div className="mt-1 text-[11px] uppercase tracking-[0.12em] text-ink-400">
          {era}
        </div>
      </div>
      <div className="relative pl-6 pb-2">
        {!last && (
          <span className="absolute left-[5px] top-3 bottom-0 w-px bg-ink-200" />
        )}
        <span
          className={`absolute left-0 top-1.5 w-3 h-3 rounded-full ring-4 ring-paper ${
            highlight ? "bg-lime-400" : "bg-ink-300"
          }`}
        />
        <div
          className={`text-[18px] font-semibold tracking-tight ${
            highlight ? "text-ink-900" : "text-ink-800"
          }`}
        >
          {title}
        </div>
        <p className="mt-1.5 text-[14px] text-ink-600 leading-relaxed max-w-[560px]">
          {body}
        </p>
      </div>
    </li>
  );
}

function RegionCard({ place, note }: { place: string; note: string }) {
  return (
    <Card className="p-4">
      <div className="text-[14px] font-semibold tracking-tight text-ink-900">
        {place}
      </div>
      <div className="mt-1 text-[12px] text-ink-600 leading-snug">{note}</div>
    </Card>
  );
}

function DarkStat({ kpi, label }: { kpi: string; label: string }) {
  return (
    <div className="rounded-2xl bg-ink-800 hairline-strong p-5">
      <div className="text-[28px] sm:text-[34px] font-semibold tracking-tight text-lime-300 tabular">
        {kpi}
      </div>
      <div className="mt-1 text-[13px] text-ink-300 leading-snug">{label}</div>
    </div>
  );
}
